/**
 * Landing copy for the Colosseum build.
 *
 * `not-licensed` exists because a regulatory position is not a model. A
 * reader can falsify that one against the Labuan FSA register, which is what
 * a status is for.
 *
 * `simulated` is doing real work here. The vault, keys, transactions, policy
 * checks, route ranking and evidence are real code with tests, but the demo
 * runs against a chain simulator by default and no devnet run has been
 * recorded yet. Anything that would only be true once a transaction lands on
 * a real cluster is marked `simulated`, not `testnet-verified`.
 */
export type ClaimStatus = 'live' | 'testnet-verified' | 'modeled' | 'simulated' | 'not-licensed';

export type Claim = {
  claim: string;
  evidence: string;
  status: ClaimStatus;
};

export const lockedCopy = {
  headline: 'Pay suppliers in Southeast Asia. Prove every payment.',
  fee: '0.33% all-in target',
  feeFootnote: 'A target the route engine ranks against, not a quoted price.',
  speed: '~400ms Solana settlement finality',
  speedFootnote: 'Local bank delivery varies by rail.',
  yield: 'Variable APY - T-bill-backed',
  agent: 'Zeke prepares. You approve.',
} as const;

export const claims = {
  heroSub: {
    claim: 'Invoice, approval, transfer and rate — one record per payment, on Solana, ready for your auditor.',
    evidence: 'Evidence bundle, approval chain and receipt verification in lib/solana-evidence and app/api/solana/receipts.',
    status: 'simulated',
  },
  labuanApplication: {
    claim: 'No licence held. A Labuan FSA money-broking application is in preparation with counsel.',
    evidence: 'Not on the Labuan FSA register — verifiable there. SECURITY.md records the truth pass.',
    status: 'not-licensed',
  },
  sumsub: {
    claim: 'Sumsub KYB workflow is wired for business verification intake.',
    evidence: 'KYB upload and Sumsub route handlers in app/api/kyb.',
    status: 'testnet-verified',
  },
  elliptic: {
    claim: 'Wallet screening is a stub. KYT and sanctions checks are a required production gate, not something this demo performs.',
    evidence: 'Screening stub in the policy step; the gate is recorded in docs/solana-build/SPEC.md.',
    status: 'modeled',
  },
  custody: {
    claim: 'Your vault, your keys. Every business gets its own Squads V4 vault with configAuthority null, and Splash holds no key that can move the money.',
    evidence: 'lib/solana/squads.ts; invariant I-1 asserted by tests/solana-chain-squads.test.mjs.',
    status: 'simulated',
  },
  finality: {
    claim: lockedCopy.speed,
    evidence: 'Solana slot time as the settlement label. The demo runs on a chain simulator by default; no devnet run is recorded yet.',
    status: 'simulated',
  },
  fee: {
    claim: lockedCopy.fee,
    evidence: 'The route engine ranks Solana-direct against a CCTP V2 burn by pesos the supplier receives (lib/route-engine). The partner rate is a fixed mock reference, not a live quote.',
    status: 'modeled',
  },
  treasuryYield: {
    claim: lockedCopy.yield,
    evidence: 'Treasury screen labels projected and modelled values; no yield is earned in this demo.',
    status: 'modeled',
  },
  receivable: {
    claim: 'Zeke reads the invoice in quarantine and compares routes. It never signs, and it cannot change an amount, a recipient or a route.',
    evidence: 'lib/solana-zeke; invariant I-4 — execute without a person’s signature returns 403 (tests/solana-app-flow.test.mjs).',
    status: 'simulated',
  },
  auditTrail: {
    claim: 'Every payment leaves one record, anchored on Solana, and anyone holding the receipt can check it themselves.',
    evidence: 'Evidence record built and anchored in lib/solana-evidence; the receipt page re-verifies it and fails when any field is changed. Simulated receipts are labelled.',
    status: 'simulated',
  },
  footerLegal: {
    claim: 'Not licensed, and not authorised to hold customer funds. Built for Colosseum: the vault, keys, transactions, policy checks, route ranking and evidence are real code with tests, the payout partner is a labelled mock and wallet screening is a stub. The demo runs on a chain simulator by default and every screen and receipt says so.',
    evidence: 'README "What is real and what is mock"; invariant I-7 — simulated receipts are labelled (tests/solana-chain-simulator.test.mjs).',
    status: 'simulated',
  },
} satisfies Record<string, Claim>;
